/**
 * `<ContextMenu>` — a minimal right-click menu for the workspace surface.
 *
 * There was no context-menu infrastructure in the app, and the theme editor
 * needs one entry point; this is deliberately the smallest thing that can grow.
 * It knows nothing about themes — it renders a tree of items and reports the
 * one that was chosen.
 *
 * Three behaviours are non-negotiable for a menu and easy to get wrong:
 *
 * - **It closes.** Escape, a click anywhere outside, or choosing an item. The
 *   outside listener is on `pointerdown` (not `click`) so the menu is gone
 *   before whatever was under the pointer reacts.
 * - **It stays on screen.** Opened near the right or bottom edge it flips back
 *   inside the viewport, because a menu half off-screen is unusable exactly
 *   when the user right-clicked the edge of a panel.
 * - **It is ephemeral.** Open/closed and which submenu is showing are component
 *   state, never workspace data (D8) — reopening a saved workspace must not
 *   reopen a menu.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";

export interface MenuItem {
  id: string;
  label: ReactNode;
  /** Chosen → the menu closes and this fires. Ignored when `items` is present. */
  onSelect?: () => void;
  /** A submenu. An item with children is a parent, not an action. */
  items?: MenuItem[];
  disabled?: boolean;
  /** Trailing hint (a checkmark, a shortcut). */
  hint?: ReactNode;
}

export interface ContextMenuProps {
  /** Viewport coordinates of the click that opened it. */
  x: number;
  y: number;
  items: MenuItem[];
  onClose: () => void;
}

const MENU_WIDTH = 190;

export function ContextMenu({ x, y, items, onClose }: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });
  const [openSub, setOpenSub] = useState<string | null>(null);

  // Flip back inside the viewport once the real size is known. Measured rather
  // than assumed, because the item labels decide the height.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPos({
      x: Math.max(4, Math.min(x, window.innerWidth - width - 4)),
      y: Math.max(4, Math.min(y, window.innerHeight - height - 4)),
    });
  }, [x, y]);

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="menu"
      // Fixed, because the coordinates come from the pointer event and are
      // viewport-relative. Absolute would silently offset the menu the day any
      // ancestor of the shell becomes a positioned element.
      style={{ ...menuStyle, position: "fixed", left: pos.x, top: pos.y }}
      // The menu is not the surface; a right-click inside it must not open a
      // second one on top of the first.
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item) => {
        const hasSub = !!item.items?.length;
        const showSub = hasSub && openSub === item.id;
        return (
          <div
            key={item.id}
            style={{ position: "relative" }}
            onPointerEnter={() => setOpenSub(hasSub ? item.id : null)}
          >
            <button
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                if (hasSub) {
                  setOpenSub((cur) => (cur === item.id ? null : item.id));
                  return;
                }
                item.onSelect?.();
                onClose();
              }}
              style={{
                ...itemStyle,
                color: item.disabled ? "var(--qf-text-muted)" : "var(--qf-text)",
                cursor: item.disabled ? "default" : "pointer",
                background: showSub ? "var(--qf-selection)" : "transparent",
              }}
            >
              <span>{item.label}</span>
              <span style={{ marginLeft: "auto", color: "var(--qf-text-muted)" }}>
                {hasSub ? "▸" : item.hint}
              </span>
            </button>

            {showSub && <SubMenu items={item.items!} onClose={onClose} />}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A submenu, hung off its parent item.
 *
 * Absolute, because the parent item is the positioned element it should follow.
 * It has to keep itself on screen the same way the menu does: the top-level
 * clamp only knows the menu's own height, so a menu opened in the bottom half
 * fitted while Settings, opening *downwards* from its row, ran off the bottom.
 * Measured, then shifted up by exactly the overflow (and flipped to the left
 * past the right edge), so it still lines up with its row whenever it can.
 */
function SubMenu({ items, onClose }: { items: MenuItem[]; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState<{ top: number; flip: boolean } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;
    const row = parent.getBoundingClientRect();
    const { width, height } = el.getBoundingClientRect();
    const overflow = row.top + height - (window.innerHeight - 4);
    // Up by the overflow, but never above the top edge.
    const top = overflow > 0 ? -Math.min(overflow, row.top - 4) : 0;
    const flip = row.right - 4 + width > window.innerWidth - 4 && row.left - width + 4 >= 4;
    setShift({ top, flip });
  }, [items]);

  return (
    <div
      ref={ref}
      role="menu"
      style={{
        ...menuStyle,
        position: "absolute",
        top: shift?.top ?? 0,
        ...(shift?.flip ? { right: "calc(100% - 4px)" } : { left: "calc(100% - 4px)" }),
        // Hidden for the one frame before it is measured, so it never paints
        // off-screen and then jumps.
        visibility: shift ? "visible" : "hidden",
      }}
    >
      {items.map((sub) => (
        <button
          key={sub.id}
          role="menuitem"
          disabled={sub.disabled}
          onClick={() => {
            sub.onSelect?.();
            onClose();
          }}
          style={{
            ...itemStyle,
            color: sub.disabled ? "var(--qf-text-muted)" : "var(--qf-text)",
            cursor: sub.disabled ? "default" : "pointer",
          }}
        >
          <span>{sub.label}</span>
          <span style={{ marginLeft: "auto", color: "var(--qf-text-muted)" }}>{sub.hint}</span>
        </button>
      ))}
    </div>
  );
}

const menuStyle: React.CSSProperties = {
  minWidth: MENU_WIDTH,
  padding: "0.25rem",
  borderRadius: 6,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface-alt)",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.35)",
  // Above the layout dividers (5) and the grid toolbar (7).
  zIndex: 40,
  fontSize: "0.8rem",
};

const itemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  width: "100%",
  padding: "0.3rem 0.5rem",
  border: "none",
  borderRadius: 4,
  background: "transparent",
  textAlign: "left",
  font: "inherit",
};